import { Component, OnInit, ViewChild } from '@angular/core';
import { MediaObserver } from '@angular/flex-layout';
import { Store, select } from '@ngrx/store';
import { BaseClass } from '@zerops/fe/core';
import { map, takeUntil, delayWhen, tap, filter, switchMap, distinctUntilChanged, withLatestFrom } from 'rxjs/operators';
import { combineLatest, Subject, timer } from 'rxjs';
import animateScrollTo from 'animated-scroll-to';
import { State } from '@app/models';
import { PermissionService } from '@zerops/fe/permission';
import { getRouterSlice } from '@app/common/ngrx-router';
import { SearchOpen } from '@app/common/search';
import { MenuItemsComponent } from '@app/common/menu';
import { overdueInvoices } from '@app/base/invoices-base';
import { environment } from 'environments/environment';
import { environment as environmentForDevel } from 'environments/environment.devel';
import { authActiveClientUser, Roles, authSlice } from '@app/base/auth-base';
import { ClientMode } from '@app/base/clients-base';
import { LocalRedirectionService } from '@app/services';
import { activeUserClient } from '@app/base/auth-base/auth-base.selector';
import { localhostRedirectStorageKey } from '@app/constants';
import { b64EncodeUnicode } from 'utils';

@Component({
  selector: 'vshcz-app-bar',
  templateUrl: './app-bar.container.html',
  styleUrls: [ './app-bar.container.scss' ]
})
export class AppBarContainer extends BaseClass implements OnInit {

  isProductionEnv = environment.envName === 'prod';

  // # Event Streams
  onSearchOpen$ = new Subject<void>();

  // # Data
  // -- sync
  _menuTransitionEnabled = false;

  // -- angular
  @ViewChild(MenuItemsComponent)
  menuItemsRef: MenuItemsComponent;

  private _authActiveClientUser$ = this._store.pipe(
    select(authActiveClientUser)
  );

  // -- async
  isPostPaidClient$ = this._authActiveClientUser$.pipe(
    filter((d) => !!d),
    map((d) => d.client.mode),
    map((mode) => mode === ClientMode.POST_PAID)
  );

  isDashboardPage$ = this._store.pipe(
    select(getRouterSlice),
    filter((s) => !!s),
    map((slice) => slice && slice.state
      ? slice.state.url.includes('/dashboard') || (slice.state.url.includes('?refreshToken') && slice.state.url.includes('&clientId'))
      : false
    ),
    distinctUntilChanged()
  );

  gpuCloudUrl$ = combineLatest([
    this.isPostPaidClient$,
    this._localRedirection.onChangeRedirect$
  ]).pipe(
      filter(([ d, _ ]) => d === false),
      switchMap(() => this._store.pipe(select(authSlice))),
      filter((d) => !!d),
      withLatestFrom(
        this._store.pipe(select(activeUserClient)),
        this._authActiveClientUser$
      ),
      map(([ authBaseState, clientEntity, clientUserEntity ]) => {
        const path = `${authBaseState.refreshToken}|${clientEntity.id}|${clientUserEntity.id}`;
        const encodedPath = encodeURIComponent(b64EncodeUnicode(path));
        const forceRedirectToLocalhost = this._getLocalhostRedirect();
        return `${(forceRedirectToLocalhost ? environmentForDevel : environment).gpuCloudAuthRedirectUrl}/${encodedPath}`;
      })
    );

  _menuHidden$ = this.isDashboardPage$.pipe(
    map((f) => !f),
    delayWhen((a) => a ? timer(250) : timer(0)),
    tap(() => {
      if (this.menuItemsRef) {
        this.menuItemsRef.refresh();
      }
    })
  );

  overdueInvoiceCount$ = this._store.pipe(
    select(overdueInvoices),
    map((invoices) => !!invoices ? invoices.length : 0)
  );

  // https://redmine.vshosting.cz/issues/14397
  // servicesCount$ = this._store.pipe(
  //   select(authActiveUserServicesActiveGrouped),
  //   map((groups) => !!groups
  //     ? groups.length
  //     : 0
  //   )
  // );

  showGraphsSettingsAndExternal$ = this._permissions.authorize({
    only: [ Roles.Manager, Roles.Technical ]
  });

  showFinancialAndStatus$ = this._permissions.authorize({
    only: [ Roles.Manager, Roles.Financial ]
  });

  // # Action Streams
  private _searchOpenAction$ = this.onSearchOpen$.pipe(
    map(() => new SearchOpen())
  );

  constructor(
    public media: MediaObserver,
    private _permissions: PermissionService,
    private _store: Store<State>,
    private _localRedirection: LocalRedirectionService
  ) {
    super();

    // # Store Dispatcher
    this._searchOpenAction$
      .pipe(takeUntil(this._ngOnDestroy$))
      .subscribe(this._store);
  }

  ngOnInit() {
    setTimeout(() => {
      this._menuTransitionEnabled = true;
    });
  }

  toTop() {
    return animateScrollTo(
      0,
      {
        maxDuration: 800
      }
    );
  }

  private _getLocalhostRedirect() {
    const localStorageVar = localStorage.getItem(localhostRedirectStorageKey);
    return !this.isProductionEnv
      && parseInt(localStorageVar
          ? localStorageVar
          : '0',
        10
      ) === 1;
  }

}
